import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Modal, Tabs, Spin, Button } from "antd";
import { ExternalLink } from "lucide-react";
import { useModalStore } from "../../../store/slices/modal";
import { MODAL_TYPES } from "../../../types/modal-types";
import { IProduct, ProductFormValues } from "../../../types/api/product";
import { useMessageApi } from "../../../provider/AntdNotification";
import {
  useProductCreate,
  useProductUpdate,
  useProductDetail,
} from "../../../hooks/api/product/hooks";
import {
  useVariantList,
  useVariantCreate,
  useVariantUpdate,
} from "../../../hooks/api/variant/hooks";
import { useMasterList } from "../../../hooks/api/master/hooks";
import { useArtistList } from "../../../hooks/api/artist/hooks";
import { useCategoryList } from "../../../hooks/api/category/hooks";
import {
  useCountryList,
  useStateList,
  useCityList,
} from "../../../hooks/api/location/hooks";
import VariantList from "./VariantList";
import { BasicInfoTab } from "./tabs/BasicInfoTab";
import { LocationTab } from "./tabs/LocationTab";

// Not config.siteUrl - see the same note in views/layout/Top.tsx.
const PUBLIC_SITE_URL = "https://artparks.co.uk";

const emptyValues: ProductFormValues = {
  title: "",
  slug: "",
  artistId: "",
  shortDescription: "",
  description: "",
  cascaderCategoryIds: [],
  categoryIds: [],
  subCategoryIds: [],
  tags: [],
  subjectId: null,
  styleId: null,
  display: "Indoor",
  location: { countryId: "", stateId: "", cityId: "" },
  isFeatured: false,
  status: true,
  artistComment: "",
  seoTitle: "",
  metaDescription: "",
  variant: {
    images: [],
    imageAlts: {},
    price: 0,
    artistPrice: 0,
    isPriceOnApplication: false,
    isForSale: true,
    materialId: null,
    editionText: "",
    isCommissioned: false,
    artistProofsAvailable: false,
    artistProofsCount: 0,
    year: null,
    dimensions: { height: 0, width: 0, depth: 0, unit: "cm" },
    weight: { value: 0, unit: "kg" },
  },
};

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: IProduct | null;
  qData?: any;
}

const ProductModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  selectedItem,
  qData,
}) => {
  const messageApi = useMessageApi();
  const [activeTab, setActiveTab] = useState("1");
  const { type } = useModalStore();

  useEffect(() => {
    if (isOpen) {
      if (type === MODAL_TYPES.EDIT_PRODUCT_VARIANTS) {
        setActiveTab("3");
      } else {
        setActiveTab("1");
      }
    }
  }, [isOpen, type]);

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<ProductFormValues>({
      defaultValues: emptyValues,
    });

  // Success/error handling (toast + close) now lives entirely in onSubmit
  // below, not here - the product save is only step one of a combined
  // product+default-edition save, and closing/toasting right after this
  // step alone would skip the edition save that follows it (and, on
  // create, orphan the new product with no default edition at all).
  const createMutation = useProductCreate({
    qData,
  });
  const updateMutation = useProductUpdate({
    qData,
  });

  const { data: detailData, isFetching: isFetchingDetail } = useProductDetail(
    selectedItem?._id || "",
    { enabled: isOpen && !!selectedItem?._id },
  );

  // Photos, price, size and material for this sculpture's first (default)
  // edition now live on the main Basic Info tab, not two steps further
  // under Editions - matches the artist-portal sculpture form (Oliver,
  // Sept 2026: "Edit Product opens without the photos, price, size or
  // material, which are two steps further in under Variants, then Edit
  // Variant... Could Edit Product work like the artist form"). Fetching
  // the existing variant list here to find (or know there isn't yet) a
  // default edition to pre-fill from and save back to.
  const { data: variantsData, isFetching: isFetchingVariants } = useVariantList(
    { productId: selectedItem?._id, limit: 100, page: 1 } as any,
    { enabled: isOpen && !!selectedItem?._id },
  );
  const defaultVariant: any = React.useMemo(
    () => (variantsData?.data || []).find((v: any) => v.isDefault) || null,
    [variantsData],
  );
  const variantCreateMutation = useVariantCreate({});
  const variantUpdateMutation = useVariantUpdate({});

  const { data: artistsData, refetchWithQuery: refetchArtist } = useArtistList(
    { limit: 100, page: 1 } as any,
    { enabled: isOpen },
  );

  const watchCountryId = watch("location.countryId");
  const watchStateId = watch("location.stateId");

  const { data: countriesData, refetchWithQuery: refetchCountry } =
    useCountryList({ limit: 100, page: 1 } as any, { enabled: isOpen });

  const { data: statesData, refetchWithQuery: refetchState } = useStateList(
    { limit: 100, page: 1, countryId: watchCountryId } as any,
    { enabled: isOpen && !!watchCountryId },
  );

  const { data: citiesData, refetchWithQuery: refetchCity } = useCityList(
    {
      limit: 100,
      page: 1,
      countryId: watchCountryId,
      stateId: watchStateId,
    } as any,
    { enabled: isOpen && !!watchStateId },
  );

  // Peter, Oct 2026: "Both Subject and style only have choices beginning
  // with A" - this is the actual initial-load fetch for both (separate
  // from BasicInfoTab's own baseQuery prop, which only feeds subsequent
  // search-triggered refetches once the admin types). Subject has 1525
  // real entries, Style 1007, sorted alphabetically - limit 20 here meant
  // the unsearched dropdown could never get past the 136 names starting
  // with "A" alone. Matches the 200 already used for Medium two lines
  // down, same large-taxonomy precedent.
  const { data: subjectsData, refetchWithQuery: refetchSubject } =
    useMasterList({ limit: 200, page: 1, type: "SUBJECT" } as any, {
      enabled: isOpen,
    });
  const { data: stylesData, refetchWithQuery: refetchStyle } = useMasterList(
    { limit: 200, page: 1, type: "STYLE" } as any,
    { enabled: isOpen },
  );
  const { data: tagsData, refetchWithQuery: refetchTag } = useMasterList(
    { limit: 100, page: 1, type: "TAGS" } as any,
    { enabled: isOpen },
  );
  const { data: materialData, refetchWithQuery: refetchMaterial } = useMasterList(
    { limit: 200, page: 1, type: "MEDIUM" } as any,
    { enabled: isOpen },
  );
  const { data: categoryData, refetchWithQuery: refetchCategory } = useCategoryList(
    { limit: 1000, page: 1 } as any,
    { enabled: isOpen },
  );

  const artistOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const a: any = detailData?.data?.artistId;
    if (a && typeof a === "object" && a._id) {
      map.set(String(a._id), { label: a.name || "Artist", value: a._id });
    }
    (artistsData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [artistsData, detailData]);

  const categoryOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const catList = detailData?.data?.categoryIds;
    if (Array.isArray(catList)) {
      catList.forEach((c: any) => {
        if (c && typeof c === "object" && c._id) {
          map.set(String(c._id), { label: c.name || "Category", value: c._id });
        }
      });
    }
    (categoryData?.data || []).forEach((c: any) => {
      if (c && c._id) map.set(String(c._id), { label: c.name, value: c._id });
    });
    return Array.from(map.values());
  }, [categoryData, detailData]);

  // Doc-shaped (keeps parentId, unlike categoryOptions above) pool for the
  // Category/Sub Category split dropdowns - always includes whatever this
  // specific product is actually pointing at (merged in from its own
  // categoryIds) on top of the general fetched list, so a category this
  // product references but that's since been soft-deleted, or otherwise
  // fell out of the general list, still resolves to a real name and a
  // selectable option instead of a raw id with nothing to match it
  // (Frankie via Oliver, Sept 2026 - "Category is blank and Sub Category
  // shows codes... instead of names, greyed out").
  const categoryPool = React.useMemo(() => {
    const map = new Map<string, any>();
    const catList = detailData?.data?.categoryIds;
    if (Array.isArray(catList)) {
      catList.forEach((c: any) => {
        if (c && typeof c === "object" && c._id) map.set(String(c._id), c);
      });
    }
    (categoryData?.data || []).forEach((c: any) => {
      if (c && c._id && !map.has(String(c._id))) map.set(String(c._id), c);
    });
    return Array.from(map.values());
  }, [categoryData, detailData]);

  const subjectOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const s: any = detailData?.data?.subjectId;
    if (s && typeof s === "object" && s._id) {
      map.set(String(s._id), { label: s.name || "Subject", value: s._id });
    }
    (subjectsData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [subjectsData, detailData]);

  const styleOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const s: any = detailData?.data?.styleId;
    if (s && typeof s === "object" && s._id) {
      map.set(String(s._id), { label: s.name || "Style", value: s._id });
    }
    (stylesData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [stylesData, detailData]);

  const tagsOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const tagList = detailData?.data?.tags;
    if (Array.isArray(tagList)) {
      tagList.forEach((t: any) => {
        if (t && typeof t === "object" && t._id) {
          map.set(String(t._id), { label: t.name || "Tag", value: t._id });
        }
      });
    }
    (tagsData?.data || []).forEach((t: any) => {
      if (t && t._id) map.set(String(t._id), { label: t.name, value: t._id });
    });
    return Array.from(map.values());
  }, [tagsData, detailData]);

  const countryOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const c: any = detailData?.data?.location?.countryId;
    if (c && typeof c === "object" && c._id) {
      map.set(String(c._id), { label: c.name || "Country", value: c._id });
    }
    (countriesData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [countriesData, detailData]);

  const materialOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const m: any = defaultVariant?.materialId;
    if (m && typeof m === "object" && m._id) {
      map.set(String(m._id), { label: m.name || "Material", value: m._id });
    }
    (materialData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [materialData, defaultVariant]);

  const stateOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const s: any = detailData?.data?.location?.stateId;
    if (s && typeof s === "object" && s._id) {
      map.set(String(s._id), { label: s.name || "State", value: s._id });
    }
    (statesData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [statesData, detailData]);

  const cityOptions = React.useMemo(() => {
    const map = new Map<string, any>();
    const ci: any = detailData?.data?.location?.cityId;
    if (ci && typeof ci === "object" && ci._id) {
      map.set(String(ci._id), { label: ci.name || "City", value: ci._id });
    }
    (citiesData?.data || []).forEach((item: any) => {
      if (item && item._id) map.set(String(item._id), { label: item.name, value: item._id });
    });
    return Array.from(map.values());
  }, [citiesData, detailData]);

  // Peter, Oct 2026: "categories & subcategories dont seem to stay unless
  // you enter them 2wice". Root cause: this effect's own dependency array
  // includes categoryPool/detailData/defaultVariant - all derived from
  // React Query results that get a brand-new object/array reference on
  // ANY background refetch (window-refocus revalidation, categoryData
  // finishing a beat after detailData on first open, etc.), not only the
  // initial load. Every one of those re-triggered this effect and called
  // reset() again with the ORIGINALLY loaded values, silently discarding
  // whatever the admin had already picked in the form since opening the
  // modal - not just Category/Sub Category, every field. A pick made
  // right before one of those refetches landed looked like it "didn't
  // take"; the next attempt, after things had settled, did. resetDoneFor
  // makes this run at most once per modal-open session for a given
  // item (or once for a fresh "Add Sculpture"), so a later background
  // refetch updates the options lists without silently rewinding
  // anything the admin has already typed or selected.
  const resetDoneFor = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab("1");
      resetDoneFor.current = null;
      return;
    }

    // Peter, Oct 2026: "the first time you open one, images aren't there /
    // description reads from the previous sculpture". detailData (product
    // fields) and variantsData (images/price/etc, used to derive
    // defaultVariant) are two independent queries that don't resolve at
    // the same moment. This effect used to reset() as soon as detailData
    // alone landed, lock resetDoneFor right after, and never run again for
    // this item - so if variantsData was still mid-flight at that instant,
    // whatever defaultVariant-derived fields it reset with (often empty)
    // were stuck like that for the rest of this open, even once the real
    // variant data arrived a beat later. Wait for both to settle before
    // resetting at all.
    if (selectedItem && detailData?.data && !isFetchingVariants) {
      if (resetDoneFor.current === selectedItem._id) return;
      const d = detailData.data as any;

      const artistIdVal =
        typeof d.artistId === "object"
          ? d.artistId?._id || ""
          : d.artistId ?? "";
      const subjectIdVal =
        typeof d.subjectId === "object"
          ? d.subjectId?._id || null
          : d.subjectId ?? null;
      const styleIdVal =
        typeof d.styleId === "object"
          ? d.styleId?._id || null
          : d.styleId ?? null;

      const parsedTags = Array.isArray(d.tags)
        ? d.tags
            .map((t: any) => (typeof t === "object" ? t._id : t))
            .filter(Boolean)
        : [];
      // Existing categoryIds is a flat mix of mains and subs - split back
      // into the two locked dropdowns using each doc's parentId.
      const rawCategoryDocs: any[] = Array.isArray(d.categoryIds) ? d.categoryIds : [];
      const explicitMainIds = rawCategoryDocs
        .filter((c: any) => typeof c === "object" && !c.parentId)
        .map((c: any) => c._id)
        .filter(Boolean);
      const parsedSubCatIds = rawCategoryDocs
        .filter((c: any) => typeof c === "object" && !!c.parentId)
        .map((c: any) => c._id)
        .filter(Boolean);
      // Many migrated sculptures only ever had sub-category ids stored,
      // no main - not a deleted-category case, just data the WP import/
      // consolidation never added a main entry for (confirmed live: e.g.
      // "The Vigil" and "Beach Song 1" have subs but zero main-type
      // entries in their stored categoryIds). Each sub still carries its
      // own real parentId though, so the main is always derivable - infer
      // it from every selected sub, rather than leaving Category blank
      // (which is what caused Sub Category's options to always compute
      // empty too, per subCategoryOptionsForSelectedMains below, and
      // AntD falling back to showing the raw id with nothing to label it).
      const poolById = new Map(categoryPool.map((c: any) => [String(c._id), c]));
      const inferredMainIds = Array.from(
        new Set(
          rawCategoryDocs
            .filter((c: any) => typeof c === "object" && !!c.parentId)
            .map((c: any) => {
              const parentId = typeof c.parentId === "object" ? c.parentId?._id : c.parentId;
              return parentId ? String(parentId) : null;
            })
            .filter((id): id is string => !!id && poolById.has(id)),
        ),
      );
      const parsedCatIds = Array.from(new Set([...explicitMainIds, ...inferredMainIds]));

      const loc = d.location || {};
      const countryIdVal = typeof loc.countryId === "object" ? loc.countryId?._id || "" : loc.countryId ?? "";
      const stateIdVal = typeof loc.stateId === "object" ? loc.stateId?._id || "" : loc.stateId ?? "";
      const cityIdVal = typeof loc.cityId === "object" ? loc.cityId?._id || "" : loc.cityId ?? "";

      reset({
        title: d.title ?? "",
        slug: d.slug ?? "",
        artistId: artistIdVal,
        shortDescription: d.shortDescription ?? "",
        description: d.description ?? "",
        cascaderCategoryIds: d.cascaderCategoryIds ?? [],
        categoryIds: parsedCatIds,
        subCategoryIds: parsedSubCatIds,
        tags: parsedTags,
        subjectId: subjectIdVal,
        styleId: styleIdVal,
        display: d.display ?? "Indoor",
        location: {
          countryId: countryIdVal,
          stateId: stateIdVal,
          cityId: cityIdVal,
        },
        isFeatured: d.isFeatured ?? false,
        status: d.status ?? true,
        artistComment: d.artistComment ?? "",
        seoTitle: d.seoTitle ?? "",
        metaDescription: d.metaDescription ?? "",
        variant: {
          images: defaultVariant?.images || [],
          imageAlts: defaultVariant?.imageAlts || {},
          price: defaultVariant?.price ?? 0,
          artistPrice: defaultVariant?.artistPrice ?? 0,
          isPriceOnApplication: defaultVariant?.isPriceOnApplication ?? false,
          isForSale: defaultVariant?.isForSale ?? true,
          materialId: defaultVariant?.materialId?._id || defaultVariant?.materialId || null,
          editionText: defaultVariant?.editionText ?? "",
          isCommissioned: defaultVariant?.isCommissioned ?? false,
          artistProofsAvailable: defaultVariant?.artistProofsAvailable ?? false,
          artistProofsCount: defaultVariant?.artistProofsCount ?? 0,
          year: defaultVariant?.year ?? null,
          // Don't fabricate 0 for a genuinely-unset legacy value - a
          // variant with no recorded height/width/depth/weight reads back
          // as e.g. {unit:"kg"} with the numeric key undefined (Mongoose
          // auto-inits the subdocument because unit has a schema
          // default), so defaulting to 0 here would silently write a
          // fabricated 0 into it the next time anyone resaves this
          // product - the real (if minor) version of the "fields nobody
          // touched getting reset" risk Oliver flagged, Sept 2026.
          dimensions: {
            height: defaultVariant?.dimensions?.height,
            width: defaultVariant?.dimensions?.width,
            depth: defaultVariant?.dimensions?.depth,
            unit: defaultVariant?.dimensions?.unit ?? "cm",
          },
          weight: {
            value: defaultVariant?.weight?.value,
            unit: defaultVariant?.weight?.unit ?? "kg",
          },
        },
      });
      resetDoneFor.current = selectedItem._id;
    } else if (!selectedItem) {
      if (resetDoneFor.current === "NEW") return;
      reset(emptyValues);
      resetDoneFor.current = "NEW";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedItem, detailData, defaultVariant, categoryPool, isFetchingVariants, reset]);

  // Tracks the id to save the default edition against once a brand-new
  // sculpture has just been created in this same submit, mirroring the
  // artist-portal form's savedProductId pattern.
  const [savedProductId, setSavedProductId] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) setSavedProductId(selectedItem?._id || null);
  }, [isOpen, selectedItem]);

  const onSubmit = async (values: ProductFormValues) => {
    // Backend only stores one flat categoryIds array - merge the two
    // locked dropdowns (Category + Sub Category) back into it here. The
    // now-redundant subCategoryIds field is harmless to still send (not
    // part of the Product schema, Mongoose drops unknown fields).
    const { variant: variantValues, ...productValues } = values;
    const productPayload = {
      ...productValues,
      categoryIds: [...(productValues.categoryIds || []), ...(productValues.subCategoryIds || [])],
    };

    try {
      let productId = selectedItem?._id || savedProductId;
      if (productId) {
        await updateMutation.mutateAsync({ id: productId, data: productPayload as any });
      } else {
        const res: any = await createMutation.mutateAsync(productPayload as any);
        productId = res?.data?._id || res?.data?.data?._id;
        if (productId) setSavedProductId(productId);
      }

      if (productId) {
        const variantPayload = { ...variantValues, productId, isDefault: true };
        if (defaultVariant?._id) {
          await variantUpdateMutation.mutateAsync({ id: defaultVariant._id, data: variantPayload as any });
        } else {
          await variantCreateMutation.mutateAsync({
            sku: null,
            currency: "GBP",
            stock: 1,
            status: true,
            ...variantPayload,
          } as any);
        }
      }

      messageApi.success(selectedItem ? "Sculpture updated successfully" : "Sculpture created successfully");
      onClose();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to save sculpture");
    }
  };

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    variantCreateMutation.isPending ||
    variantUpdateMutation.isPending;

  const items = [
    {
      key: "1",
      label: "Basic Info",
      children: (
        <form id="product-form" onSubmit={handleSubmit(onSubmit)}>
          <BasicInfoTab
            control={control}
            setValue={setValue}
            selectedItem={selectedItem}
            artistOptions={artistOptions}
            artistsData={artistsData}
            refetchArtist={refetchArtist}
            categoryOptions={categoryOptions}
            categoryData={categoryData}
            categoryPool={categoryPool}
            refetchCategory={refetchCategory}
            subjectOptions={subjectOptions}
            subjectsData={subjectsData}
            refetchSubject={refetchSubject}
            styleOptions={styleOptions}
            stylesData={stylesData}
            refetchStyle={refetchStyle}
            tagsOptions={tagsOptions}
            tagsData={tagsData}
            refetchTag={refetchTag}
            materialOptions={materialOptions}
            materialData={materialData}
            refetchMaterial={refetchMaterial}
          />
        </form>
      ),
    },
    {
      key: "2",
      label: "Location",
      children: (
        <form id="product-form" onSubmit={handleSubmit(onSubmit)}>
          <LocationTab
            control={control}
            setValue={setValue}
            countriesData={countriesData}
            countryOptions={countryOptions}
            refetchCountry={refetchCountry}
            statesData={statesData}
            stateOptions={stateOptions}
            refetchState={refetchState}
            citiesData={citiesData}
            cityOptions={cityOptions}
            refetchCity={refetchCity}
            watchCountryId={watchCountryId}
            watchStateId={watchStateId}
          />
        </form>
      ),
    },
    {
      key: "3",
      // Oliver (client feedback, Sept 2026): "Edit Product opens without
      // the photos, price, size or material, which are two steps further
      // in under Variants, then Edit Variant... Could Edit Product work
      // like the artist form, with the photos, price, size, material and
      // those fields on the main window, variants called Editions." Those
      // core fields for the sculpture's first (default) edition now live
      // on Basic Info, saved together with the product in one submit -
      // this tab is for adding further sizes/editions beyond that first
      // one (or setting Finish/Method/Base, which aren't on Basic Info).
      label: "Editions",
      children: selectedItem ? (
        <VariantList productId={selectedItem._id} />
      ) : (
        <div className="p-4 text-center text-slate-500 bg-slate-50 rounded-md border border-dashed border-slate-300">
          Save this sculpture first using the button below, then come back
          to this tab to add further sizes or editions beyond the first
          one.
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        selectedItem?.slug ? (
          <div className="flex items-center justify-between pr-8">
            <span>Edit Sculpture</span>
            <a
              href={`${PUBLIC_SITE_URL}/sculpture/${selectedItem.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs font-normal text-slate-500 hover:text-primary"
            >
              View on site <ExternalLink size={12} />
            </a>
          </div>
        ) : selectedItem ? (
          "Edit Sculpture"
        ) : (
          "Add Sculpture"
        )
      }
      open={isOpen}
      onCancel={onClose}
      onOk={handleSubmit(onSubmit)}
      confirmLoading={isPending}
      width={900}
      destroyOnClose
      style={{ top: 20 }}
      styles={{
        body: { maxHeight: "80vh", overflowY: "auto" },
      }}
      footer={
        activeTab === "3" ? null : (
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit(onSubmit)}
              loading={isPending}
            >
              Save Sculpture
            </Button>
          </div>
        )
      }
    >
      {isFetchingDetail || (!!selectedItem?._id && isFetchingVariants && resetDoneFor.current !== selectedItem._id) ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : (
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} />
      )}
    </Modal>
  );
};

export default ProductModal;
